import { useEffect, useState } from "react";
import { Typography, Box } from "@mui/material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import ContractsTable from "../../components/tables/ContractsTable.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../components/MDDForm/components/ContentDialog.jsx";
import { Form, FormRenderer } from "../../components/MDDForm/MDDForm.jsx";
import ObjektImportPreviewDialog from "./components/ObjektImportPreviewDialog.jsx";
import { fdefContractAssignedClerk } from "../../formdefs/contract_clerk.js";
import { fdefContractBulkAssignedClerk } from "../../formdefs/contract_bulk_clerk.js";
import { getRWObjectCopy } from "../../components/MDDForm/helper.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useContractActions } from "../../hooks/useContractActions.jsx";

/**
 * Schaden List Page
 * Shows a list of damages/claims
 */
export default function ContractIndexPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();
  const [contracts_reload_token, setContractsReloadToken] = useState(0);
  const [objektImportContract, setObjektImportContract] = useState(null);
  const { updateAssignedClerk, replaceAssignedClerkGlobally } =
    useContractActions();

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Verträge", path: "/admin/vertraege" },
    ]);
  }, [setBreadcrumbs]);

  const closeDialog = () => {
    setOpenCD(false);
  };

  const showEditCreateModal = (
    model,
    fdef,
    title,
    submitCallback,
    submitLabel = "Speichern",
    note = ""
  ) => {
    setOpenCD(true);
    setTca({
      title: title,
      content: (
        <Form>
          {note && (
            <Box sx={{ mb: 2 }}>
              <Typography color="text.secondary">{note}</Typography>
            </Box>
          )}
          <FormRenderer
            fdef={fdef}
            model={model}
            variant="filled"
            editMode={true}
            showActions={false}
            showCancel={true}
            showToggleEdit={false}
            showErrorText={true}
            actionsPosition="bottom"
            apiGet={backendGet}
            cancelAction={closeDialog}
            submitAction={{
              method: "callback",
              label: submitLabel,
              callback: async (formData) => {
                const result = await submitCallback(formData);
                if (result) {
                  closeDialog();
                }
              },
            }}
            setActions={setCDActions}
          />
        </Form>
      ),
      actions: null,
    });
  };

  const openUploadObjectListModal = (row = {}) => {
    setObjektImportContract({
      id: row.id,
      id_ov: row.id_ov,
      label: row.gesellschaft?.name + " | VSNR: " + row.vertragsnummer,
    });
  };

  const closeUploadObjectListModal = () => {
    setObjektImportContract(null);
  };

  const openAssignedClerkModal = (row = {}) => {
    const fdef = getRWObjectCopy(fdefContractAssignedClerk);
    showEditCreateModal(
      {
        id: row.id,
        vertrag: row.gesellschaft?.name + " | VSNR: " + row.vertragsnummer,
        sachbearbeiter_id: row.sachbearbeiter_id,
      },
      fdef,
      "Sachbearbeiter ändern",
      async (formData) => {
        return await updateAssignedClerk(row.id, {
          sachbearbeiter_id: formData.sachbearbeiter_id,
        });
      }
    );
  };

  const openBulkAssignedClerkModal = () => {
    const fdef = getRWObjectCopy(fdefContractBulkAssignedClerk);
    showEditCreateModal(
      {},
      fdef,
      "Sachbearbeiter global ersetzen",
      async (formData) => {
        const result = await replaceAssignedClerkGlobally({
          current_sachbearbeiter_id: formData.current_sachbearbeiter_id,
          new_sachbearbeiter_id: formData.new_sachbearbeiter_id,
        });

        if (result) {
          setContractsReloadToken((current_value) => current_value + 1);
        }

        return result;
      },
      "Ersetzen",
      "Hinweis: Im Moment müssen Benutzer eine OpenViva-ID haben, damit sie in der Liste angezeigt werden. Alle bestehenden Schäden des ersetzten Sachbearbeiters werden dem neuen Sachbearbeiter zugeordnet."
    );
  };

  return (
    <>
      <Box>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" gutterBottom>
            Verträge
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Übersicht aller Verträge
          </Typography>
        </Box>

        <ContractsTable
          onRowClick={openUploadObjectListModal}
          onAssignedClerkClick={openAssignedClerkModal}
          onBulkAssignedClerkClick={openBulkAssignedClerkModal}
          reloadToken={contracts_reload_token}
        />
      </Box>
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        CDActions={CDActions}
        tca={tca}
      />
      <ObjektImportPreviewDialog
        open={!!objektImportContract}
        onClose={closeUploadObjectListModal}
        vertrag={objektImportContract}
        onSuccess={() => {
          setContractsReloadToken((current_value) => current_value + 1);
        }}
      />
    </>
  );
}
